import { useCallback, useRef } from 'react';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { useNotify } from '@hotjar/hotsauce';
import { useGate, useTierForGate } from '@hotjar/allowances';
import { useCurrentSite, useCurrentSiteId } from '@hotjar/site';
import { upsell } from '@hotjar/webapp/domains/revenue/upsell';

import type { MutableRefObject } from 'react';
import type { ButtonProps, PlanBadgeProps } from '@hotjar/hotsauce';
import type { AddImageButtonProps } from './AddImageButton';
import { getImageAsBase64 } from '../../../utils';

type UseAddImageButton = (props: AddImageButtonProps) => {
  label: string;
  icon: ButtonProps['icon'];
  info: string;
  fileInputRef: MutableRefObject<HTMLInputElement | null>;
  hasPlanBadge: PlanBadgeProps['kind'] | null;
  onClickButton: () => void;
  onChangeFile: () => void;
};

type ErrorMessageKey = 'load' | 'size' | 'format';

const MAX_FILE_SIZE = 2 * 1024 ** 2; // 2 MB
const ACCEPTED_FORMATS = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

const validateFile = (file: File | undefined) => {
  if (file === undefined) throw new Error('load');
  if (file.size > MAX_FILE_SIZE) throw new Error('size');
  if (!ACCEPTED_FORMATS.includes(file.type)) throw new Error('format');
};

const useAddImageButtonText = (hasImageURL: boolean) => {
  const { t } = useTranslationWithPrefix('feedback:wizard.section.questions.image');

  const label = hasImageURL ? t('remove') : t('add');
  const icon: ButtonProps['icon'] = hasImageURL ? 'trash' : 'image';
  const info = t('info');

  const getErrorMessage = useCallback(
    (messageKey: ErrorMessageKey) => {
      return t(`${messageKey}-error`);
    },
    [t],
  );

  return { label, icon, info, getErrorMessage };
};

export const useAddImageButton: UseAddImageButton = ({ imageURL, onChangeImage }) => {
  const { data: site } = useCurrentSite();
  const currentSiteId = useCurrentSiteId();
  const { showToast } = useNotify();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasImageURL = typeof imageURL === 'string' && imageURL.length > 0;

  const { getErrorMessage, ...textProps } = useAddImageButtonText(hasImageURL);

  const hasImageAllowance = useGate('feedback.survey_question_images', {
    id: currentSiteId,
    type: 'site',
  });

  const planRequiredForImage = useTierForGate('feedback.survey_question_images', {
    id: currentSiteId,
    type: 'site',
  });

  const hasPlanBadge = hasImageAllowance === true ? null : planRequiredForImage;

  const showErrorNotification = useCallback(
    (errorMessageKey: ErrorMessageKey) => {
      showToast({ text: getErrorMessage(errorMessageKey), kind: 'danger' });
    },
    [showToast, getErrorMessage],
  );

  const resetInputFile = useCallback((objectURL: string | undefined) => {
    if (fileInputRef.current !== null) fileInputRef.current.value = '';
    if (objectURL !== undefined) URL.revokeObjectURL(objectURL);
  }, []);

  const onChangeFile = useCallback(async () => {
    const fileInput = fileInputRef.current;
    if (fileInput?.files?.[0] === undefined) return;
    let imageObjectURL: string | undefined;
    try {
      const [file] = fileInput.files;
      validateFile(file);
      const imageBase64 = await getImageAsBase64(file);
      imageObjectURL = URL.createObjectURL(file);
      onChangeImage({ imageBase64, imageObjectURL });
    } catch (error) {
      resetInputFile(imageObjectURL);
      const messageKey = (error instanceof Error ? error.message : 'load') as ErrorMessageKey;
      showErrorNotification(messageKey);
    }
  }, [onChangeImage, showErrorNotification, resetInputFile]);

  const showUpsell = useCallback(() => {
    if (site) {
      upsell({ referrer: 'survey.question.images', site });
    }
  }, [site]);

  const onClickButton = useCallback(() => {
    const fileInput = fileInputRef.current;
    if (hasImageURL) {
      resetInputFile(imageURL);
      onChangeImage({ imageBase64: undefined, imageObjectURL: undefined });
    } else if (hasImageAllowance === true) {
      fileInput?.click();
    } else {
      showUpsell();
    }
  }, [hasImageAllowance, hasImageURL, imageURL, onChangeImage, resetInputFile, showUpsell]);

  return { ...textProps, fileInputRef, hasPlanBadge, onClickButton, onChangeFile };
};
